import {
  ProjectMarkupButtonComponents,
  ProjectMarkupTableBulkName,
} from "@components/common/faro-table/faro-table-types";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { Markup } from "@custom-types/project-markups-types";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { BulkDeleteMarkups } from "@pages/project-details/project-markups/project-markups-bulk-actions/bulk-delete-markups";
import { BulkExportMarkups } from "@pages/project-details/project-markups/project-markups-bulk-actions/bulk-export-markups";
import { getProjectByIdSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { useMemo } from "react";

interface Props extends BaseProjectIdProps {
  /** List of markups to show in table */
  markups: Markup[];
}

/** List all the bulk action components for project markup table */
export function ProjectMarkupBulkActions({
  markups,
  projectId,
}: Props): ProjectMarkupButtonComponents {
  const selectedEntities = useAppSelector(selectedEntitiesSelector("markups"));
  const isAnyRowSelected = selectedEntities.length !== 0;
  const { hasPermissionToEditMarkup } = useMarkupContext();
  const selectedProject = useAppSelector(getProjectByIdSelector(projectId));

  const allowedButtonsBasedOnRowSelection: ProjectMarkupTableBulkName[] =
    useMemo(() => {
      const allowedButtons: ProjectMarkupTableBulkName[] = ["export"];

      if (isAnyRowSelected && hasPermissionToEditMarkup) {
        allowedButtons.push("deleteMarkup");
      }
      return allowedButtons;
    }, [isAnyRowSelected, hasPermissionToEditMarkup]);

  const { shouldShowBulkButtons } = useShouldShowBulkAction({
    allowedButtonsBasedOnRowSelection,
  });

  return {
    deleteMarkup: shouldShowBulkButtons("deleteMarkup") ? (
      <BulkDeleteMarkups
        selectedMarkups={isAnyRowSelected ? selectedEntities : []}
        projectId={projectId}
      />
    ) : null,

    export: shouldShowBulkButtons("export") ? (
      <BulkExportMarkups
        selectedMarkups={isAnyRowSelected ? selectedEntities : markups}
        projectName={selectedProject?.name ?? ""}
        areEntitiesSelected={isAnyRowSelected}
      />
    ) : null,
  };
}
